import React from "react";
import { trackAmplitudeEvent } from "../../amplitude";
import Icon from "../Icon";
import ProgressBar from "../ProgressBar";
import Toast from "../Toast";
import { reviewsBeyondFreeLimit } from "./todaysPlanService";
import { CollectionSettingsModal, DailyTotalsModal, PlayStyleModal } from "./TodaysPlanModals";
import useTodaysPlanConfig from "./useTodaysPlanConfig";

const MoveButtons = ({ isFirst, isLast, onMoveDown, onMoveUp, testIdPrefix }) => (
  <div className="btn-group">
    <button aria-label="Move up" className="btn btn-default btn-sm" data-testid={`${testIdPrefix}-move-up`} disabled={isFirst} onClick={onMoveUp}>
      <Icon name="chevron-up" />
    </button>
    <button aria-label="Move down" className="btn btn-default btn-sm" data-testid={`${testIdPrefix}-move-down`} disabled={isLast} onClick={onMoveDown}>
      <Icon name="chevron-down" />
    </button>
  </div>
);

const ActionButton = ({ icon, isActive = false, label, onClick, testId }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
    <button aria-label={label} className={`btn btn-default ${isActive ? "active" : ""}`} data-testid={testId} onClick={onClick} style={{ height: 48, width: 48 }}>
      <Icon name={icon} type="fa" />
    </button>
    <small className={isActive ? "text-success" : "text-secondary"}>{label}</small>
  </div>
);

const PlayedCount = ({ completed, testId, total }) => (
  <small className="text-secondary" data-testid={testId}><strong>{completed}</strong>/{total} played</small>
);

const SectionHeader = ({ count, isAllEnabled, isFirst, isReorderMode, label, onMoveDown, onMoveUp, onToggleAll, testId }) => (
  <div className="tw-flex tw-items-center tw-gap-3" style={{ borderBottom: "1px solid #ddd", borderTop: isFirst ? "none" : "1px solid #ddd", minHeight: 48 }}>
    <label className="tw-flex tw-flex-1 tw-items-center tw-gap-3" style={{ cursor: "pointer", margin: 0 }}>
      <input checked={isAllEnabled} data-testid={testId} onChange={onToggleAll} style={{ margin: 0 }} type="checkbox" />
      <span className="joystix">{label}</span>
      <span className="text-secondary">({count})</span>
    </label>
    {isReorderMode && <MoveButtons isFirst={isFirst} isLast={!isFirst} onMoveDown={onMoveDown} onMoveUp={onMoveUp} testIdPrefix={`section-${testId}`} />}
  </div>
);

const PlanRow = ({ index, isLast, isReorderMode, onOpenSettings, onReorder, onToggle, row, rowCount }) => {
  const key = `${row.collectionId}-${row.type}`;
  const isDone = row.total > 0 && row.completed >= row.total;
  const percent = row.total > 0 ? Math.min(100, Math.round((row.completed / row.total) * 100)) : 0;

  return (
    <div className="tw-py-2" data-testid={`settings-collection-row-${key}`} style={{ borderBottom: isLast ? "none" : "1px solid #eee" }}>
      <div className="tw-flex tw-items-center tw-gap-3">
        <label className="tw-flex tw-flex-1 tw-items-center tw-gap-3" style={{ cursor: "pointer", fontWeight: 500, margin: 0, minHeight: 48 }}>
          <input checked={row.enabled} data-testid={`toggle-collection-${key}`} onChange={onToggle} style={{ margin: 0 }} type="checkbox" />
          <span className={isDone ? "text-secondary" : ""} style={{ textDecoration: isDone ? "line-through" : "none" }}>{row.name}</span>
        </label>
        {isReorderMode && rowCount > 1 && (
          <MoveButtons
            isFirst={index === 0}
            isLast={index === rowCount - 1}
            onMoveDown={() => onReorder(index, "down")}
            onMoveUp={() => onReorder(index, "up")}
            testIdPrefix={key}
          />
        )}
        {!isReorderMode && (
          <button aria-label="Collection settings" className="btn btn-default btn-sm" data-testid={`settings-collection-chevron-${key}`} onClick={onOpenSettings}>
            <Icon name="sliders" type="fa" />
          </button>
        )}
      </div>
      {row.enabled && (
        <div>
          <ProgressBar percent={percent} style={{ height: 10, marginBottom: 4 }} />
          <PlayedCount completed={row.completed} testId={`settings-collection-numerical-progress-${key}`} total={row.total} />
        </div>
      )}
    </div>
  );
};

const PlanSection = ({ isFirst, isReorderMode, onOpenSettings, planConfig, reviewsOverFreeLimit, section }) => (
  <div data-testid={section.testId}>
    <SectionHeader
      count={section.count}
      isAllEnabled={section.rows.every((row) => row.enabled)}
      isFirst={isFirst}
      isReorderMode={isReorderMode}
      label={section.label}
      onMoveDown={() => planConfig.moveSection(section.type, "down")}
      onMoveUp={() => planConfig.moveSection(section.type, "up")}
      onToggleAll={() => planConfig.toggleAll(section.type)}
      testId={section.toggleTestId}
    />
    {reviewsOverFreeLimit > 0 && (
      <p className="text-secondary tw-py-2" data-testid="untapped-reviews">
        <small>{reviewsOverFreeLimit} reviews in your queue are beyond the daily free limit.</small>
      </p>
    )}
    {section.rows.map((row, index) => (
      <PlanRow
        index={index}
        isLast={index === section.rows.length - 1}
        isReorderMode={isReorderMode}
        key={`${row.collectionId}-${row.type}`}
        onOpenSettings={() => onOpenSettings(`${row.collectionId}-${row.type}`)}
        onReorder={(rowIndex, direction) => planConfig.reorderRow(row.type, rowIndex, direction)}
        onToggle={() => planConfig.toggleRow(row)}
        row={row}
        rowCount={section.rows.length}
      />
    ))}
  </div>
);

export default function TodaysPlanPanel({
  availability,
  collections,
  config,
  defaultMaxReviewsPerCollectionPerDay,
  isPro,
  onConfigChange,
  onOpenReviewSettings,
  queue
}) {
  const [isReorderMode, setIsReorderMode] = React.useState(false);
  const [settingsRowKey, setSettingsRowKey] = React.useState(null);
  const [isTotalsVisible, setIsTotalsVisible] = React.useState(false);
  const [isPlayStyleVisible, setIsPlayStyleVisible] = React.useState(false);
  const [playStyleDraft, setPlayStyleDraft] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const showNotice = (message) => setNotice({ id: Date.now(), message });
  const planConfig = useTodaysPlanConfig({ collections, config, onConfigChange, onNotice: showNotice, queue });
  const { newWordsRows, reviewRows } = planConfig;

  const reviewsLeftInPlan = queue.filter((i) => i.type === "review").reduce((s, i) => s + (i.total - i.completed), 0);
  const plannedNewWords = newWordsRows.filter((row) => row.enabled).reduce((s, row) => s + row.total, 0);
  const reviewsOverFreeLimit = isPro ? 0 : reviewsBeyondFreeLimit(collections, config, queue, availability);
  const settingsRow = [...reviewRows, ...newWordsRows].find((row) => `${row.collectionId}-${row.type}` === settingsRowKey);

  const openPlayStyle = () => {
    trackAmplitudeEvent("todays_plan_default_play_style_opened");
    setIsReorderMode(false);
    setIsPlayStyleVisible(true);
  };

  const sections = [
    { count: reviewsLeftInPlan, label: "Reviews", rows: reviewRows, testId: "settings-reviews-section", toggleTestId: "toggle-all-reviews", type: "review" },
    { count: plannedNewWords, label: "New words", rows: newWordsRows, testId: "settings-new-words-section", toggleTestId: "toggle-all-new-words", type: "new_words" }
  ];

  return (
    <div className="tw-mt-4">
      <div className="tw-flex tw-items-start tw-gap-6 tw-mb-4">
        <ActionButton icon="tachometer" label="Totals" onClick={() => { setIsReorderMode(false); setIsTotalsVisible(true); }} testId="daily-totals-button" />
        <ActionButton icon="arrows-v" isActive={isReorderMode} label="Reorder" onClick={() => setIsReorderMode(!isReorderMode)} testId="reorder-toggle" />
        <ActionButton icon="sliders" label="Play style" onClick={openPlayStyle} testId="default-play-style-banner" />
      </div>

      {(config.newWordsFirst ? [...sections].reverse() : sections).map((section, index) => (
        <PlanSection
          isFirst={index === 0}
          isReorderMode={isReorderMode}
          key={section.type}
          onOpenSettings={setSettingsRowKey}
          planConfig={planConfig}
          reviewsOverFreeLimit={section.type === "review" ? reviewsOverFreeLimit : 0}
          section={section}
        />
      ))}

      {notice && <Toast content={notice.message} context="info" key={notice.id} maxWidth={320} />}
      {isTotalsVisible && (
        <DailyTotalsModal
          defaultMaxReviewsPerCollectionPerDay={defaultMaxReviewsPerCollectionPerDay}
          newWordsRows={newWordsRows}
          onHidden={() => setIsTotalsVisible(false)}
          onOpenReviewSettings={onOpenReviewSettings}
          onSave={planConfig.setSectionTotals}
          reviewRows={reviewRows}
        />
      )}
      {isPlayStyleVisible && (
        <PlayStyleModal
          availability={availability}
          collections={collections}
          config={config}
          draft={playStyleDraft}
          onApply={planConfig.applyPlayStyle}
          onDraftChange={setPlayStyleDraft}
          onHidden={() => setIsPlayStyleVisible(false)}
        />
      )}
      {settingsRow && (
        <CollectionSettingsModal
          availability={availability}
          collection={collections.find((c) => String(c.id) === settingsRow.collectionId)}
          collectionConfig={config.collections.find((cc) => cc.collectionId === settingsRow.collectionId)}
          onCountChange={(count) => planConfig.setCollectionCount(settingsRow.type, settingsRow.collectionId, count)}
          onHidden={() => setSettingsRowKey(null)}
          onPlayOptionsChange={(updates) => planConfig.updatePlayOptions(settingsRow.collectionId, updates)}
          row={settingsRow}
        />
      )}
    </div>
  );
}
